import "./Clients.scss";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import { useTranslation } from "react-i18next";
import SearchInput from "../../components/SearchInput/SearchInput";
import React, { useEffect, useState } from "react";
import ClientsAddNewPopup from "./ClientsAddNewPopup/ClientsAddNewPopup";
import { openSuccessMessage } from "../../store/message/messageSlice";
import useAppDispatch from "../../hooks/useAppDispatch";
import { ClientsService } from "../../services/clients.service";
import type { GetProp, TableProps } from "antd";
import { Table } from "antd";
import { IUserForClientsTable } from "../../models/User";
import dots from "../../assets/images/icons/dots.svg";
import personIcon from "../../assets/images/icons/person.svg";
import basketIcon from "../../assets/images/icons/basket-black.svg";
import { useNavigate } from "react-router-dom";
import ConfirmPopup from "../../components/ConfirmPopup/ConfirmPopup";
import { handleError } from "../../helper/errorHandler";

type ColumnsType<T> = TableProps<T>["columns"];
type TablePaginationConfig = Exclude<GetProp<TableProps, "pagination">, boolean>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, "onChange">>[1];
}

const Clients = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [showAddNewClientPopup, setShowAddNewClientPopup] = useState(false);
  const [selectedIdForDeleteClient, setSelectedIdForDeleteClient] = useState<string | null>(null);

  const [data, setData] = useState<IUserForClientsTable[]>();
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    getClients(tableParams.pagination?.current, tableParams.pagination?.pageSize, searchValue);
  }, [tableParams.pagination?.current, tableParams.pagination?.pageSize]);

  const handleTableChange: TableProps<IUserForClientsTable>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const columns: ColumnsType<IUserForClientsTable> = [
    {
      title: t("FirstName"),
      dataIndex: "firstname",
    },
    {
      title: t("LastName"),
      dataIndex: "lastname",
    },
    {
      title: t("Email"),
      dataIndex: "email",
    },
    {
      title: t("Phone"),
      render: (data: IUserForClientsTable) => {
        if (!data.contactInformation.phoneCountryCode && !data.contactInformation.phoneNumber)
          return "-";
        return `${data.contactInformation.phoneCountryCode ? data.contactInformation.phoneCountryCode : ""}${data.contactInformation.phoneNumber}`;
      },
    },
    {
      title: t("Verification"),
      render: (data: IUserForClientsTable) => {
        if (data.isVerified) {
          return <span className={"status green"}>{t("Verified")}</span>;
        }
        return <span className={"status red"}>{t("Unverified")}</span>;
      },
    },
    {
      title: t("Subscription"),
      render: (data: IUserForClientsTable) => {
        if (!data.userSubscription) return "-";
        return `${data.userSubscription.subscription.credits} ${t("Credits")}`;
      },
    },
    {
      title: t("Status"),
      render: (data: IUserForClientsTable) => {
        return <span className={`status ${data.status}`}>{t(data.status)}</span>;
      },
    },
    {
      title: t("Action"),
      render: (data: IUserForClientsTable) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"} onClick={() => navigate(`/clients/${data.id}`)}>
                <img src={personIcon} alt={"profile"} />
                {t("SeeProfile")}
              </li>
              <li className={"dropdown-item"}>
                <img src={basketIcon} alt={"delete"} />
                {t("DeleteSubscription")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForDeleteClient(data.id)}>
                <img src={basketIcon} alt={"delete"} />
                {t("DeleteClient")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  const getClients = async (
    currentPage: number | undefined,
    pageSize: number | undefined,
    searchValue: string
  ) => {
    try {
      setLoading(true);
      const response = await ClientsService.getAllClients(searchValue, currentPage, pageSize);
      if (response.status === 200) {
        setLoading(false);
        setData(response.data.customers);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: response.data.total,
          },
        });
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const searchHandler = () => {
    getClients(tableParams.pagination?.current, tableParams.pagination?.pageSize, searchValue);
  };

  const clickOnReset = () => {
    getClients(tableParams.pagination?.current, tableParams.pagination?.pageSize, "");
  };

  const clickOnAddNewHandler = () => {
    setShowAddNewClientPopup(true);
  };

  const closeAddNewClientPopup = () => {
    setShowAddNewClientPopup(false);
    getClients(tableParams.pagination?.current, tableParams.pagination?.pageSize, searchValue);
  };

  const deleteClient = async () => {
    if (!selectedIdForDeleteClient) return;
    try {
      setLoading(true);
      const response = await ClientsService.deleteClient(selectedIdForDeleteClient);
      if (response.status === 200) {
        setLoading(false);
        setSelectedIdForDeleteClient(null);
        getClients(tableParams.pagination?.current, tableParams.pagination?.pageSize, searchValue);
        dispatch(openSuccessMessage(t("ClientDeletedSuccessfully")));
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const onClickNo = () => {
    setSelectedIdForDeleteClient(null);
  };

  return (
    <>
      <div className={"page-content clients-page"}>
        <MainTitle text={t("Clients")} />

        <SearchInput
          addNewBtn={true}
          onClickSearch={searchHandler}
          inputValue={searchValue}
          setInputValue={setSearchValue}
          onClickAddNew={clickOnAddNewHandler}
          clickOnReset={clickOnReset}
        />

        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      {showAddNewClientPopup && <ClientsAddNewPopup clickOnClose={closeAddNewClientPopup} />}
      {selectedIdForDeleteClient && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteTheClient")}
          onClickYes={deleteClient}
          onClickNo={onClickNo}
        />
      )}
    </>
  );
};

export default Clients;
